import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../providers/UserProvider';
import { useMetrics } from '../providers/MetricsProvider';
import { usePlatformWebsocket } from '../providers/PlatformWebsocketProvider';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { Share2, Zap, Instagram, BarChart3, Target, ArrowUpRight, Flame, Trophy } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import TiktokIcon from '@/components/ui/TiktokIcon';
import WelcomeModal from './WelcomeModal';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Enhanced animations and transitions
const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: SPRING_CONFIG
  }
};

export default function DashboardPage() {
  const { user } = useUser();
  const { progress, connectSocket } = usePlatformWebsocket();
  const { stats, loading, error, fetchMetrics, topPerformers } = useMetrics();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('this_week');
  const [welcomeModalConfig, setWelcomeModalConfig] = useState({
    title: '',
    message: '',
    ctaText: '',
    onCtaClick: () => {},
  });
  const [refreshing, setRefreshing] = useState(false);
  const pullToRefreshRef = useRef(null);
  const startY = useRef(0);
  const contentRef = useRef(null);

  // Intersection Observer hooks for scroll animations
  const [metricsRef, metricsInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [insightsRef, insightsInView] = useInView({ threshold: 0.1, triggerOnce: true });

  // Pull-to-refresh logic
  useEffect(() => {
    const element = pullToRefreshRef.current;
    
    const handleTouchStart = (e) => {
      startY.current = e.touches[0].pageY;
    };

    const handleTouchMove = (e) => {
      const currentY = e.touches[0].pageY;
      const diff = currentY - startY.current;
      
      if (diff > 50 && window.scrollY === 0 && !refreshing) {
        setRefreshing(true);
        // Simulate refresh
        setTimeout(() => {
          setRefreshing(false);
        }, 1500);
      }
    };

    if (element) {
      element.addEventListener('touchstart', handleTouchStart);
      element.addEventListener('touchmove', handleTouchMove);
    }

    return () => {
      if (element) {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [refreshing]);

  const processingPlatforms = user?.platforms?.filter(p => p.processing) || [];
  const isProcessing = processingPlatforms.length > 0;

  useEffect(() => {
    // Check if it's the user's first visit
    const hasVisitedBefore = localStorage.getItem('hasVisitedDashboard');
    
    if (!hasVisitedBefore) {
      setWelcomeModalConfig({
        title: user?.username ? `Welcome ${user.username}! 🎉` : "Welcome Creator! 🎉",
        message: "We're excited to help you manage and grow your social media presence. Start by exploring our cross-posting features or connecting your social accounts.",
        ctaText: "Let's Get Started",
        onCtaClick: () => {
          setShowWelcomeModal(false);
          localStorage.setItem('hasVisitedDashboard', 'true');
        },
      });
      setShowWelcomeModal(true);
    }
  }, [user]);

  useEffect(() => {
    fetchMetrics(selectedPeriod);
  }, [selectedPeriod, fetchMetrics]);

  useEffect(() => {
    if (isProcessing) {
      connectSocket();
    }
  }, [isProcessing, user]);

  // If processing, show the processing UI
  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md w-full space-y-8">
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-foreground">
              Importing Your Stats
            </h2>

            {processingPlatforms.map((platform) => (
              <div key={platform.uid} className="bg-card p-6 rounded-lg shadow-sm space-y-4">
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="flex flex-col items-center justify-center gap-1 text-foreground">
                    {platform.name === 'tiktok' ? <TiktokIcon /> : <Instagram className="w-8 h-8" />}
                    <p className="text-sm font-medium">@{platform.username}</p>
                  </div>
                  <Loader2 className="h-8 w-8 animate-spin text-foreground" />
                </div>
                
                <div className="space-y-2">
                  <Progress value={progress[platform.uid] || platform.progress} className="h-3" />
                  <p className="text-sm text-muted-foreground">
                    {progress[platform.uid] || platform.progress}% Complete
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const contentInsights = [
    {
      title: "Top Performing Content",
      metrics: topPerformers ? topPerformers : [
        ["Most Viewed", {}],
        ["Most Engaged", {}]
      ],
    }
  ];

  const influenceMetrics = {
    score: 78,
    level: "Rising Star",
    metrics: [
      { name: "Engagement Rate", value: "8.2%", trend: "up", change: "+2.1%" },
      { name: "Reach", value: "142K", trend: "up", change: "+15.3%" },
      { name: "Brand Deals", value: "4", trend: "up", change: "+2" }
    ],
    nextLevel: {
      name: "Influencer",
      progress: 78,
      remaining: "2.2K followers"
    }
  };

  return (
    <div className={cn(
      "min-h-screen bg-[#0B0F15]",
      "pt-[var(--navbar-height,_76px)]",
      "pb-[env(safe-area-inset-bottom,_0px)]",
      "overflow-x-hidden"
    )}>
      {/* Hero Section */}
      <div className="bg-[#151A21] p-3 border-b border-gray-800">
        <div className="max-w-full px-5 pb-2">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 text-transparent bg-clip-text mb-2">
            Creator Stats
          </h1>
          <p className="text-gray-300 text-base">
            Your influence is growing! Here's your creator performance at a glance.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="h-full flex flex-col">
        {/* Content Area */}
        <div ref={contentRef} className="flex-1 overflow-y-auto -mx-4 px-4">
          {loading ? (
            <div className="flex items-center justify-center h-[calc(100vh-var(--navbar-height)-8rem)] p-4">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white mb-4">
                  <Loader2 className="h-6 sm:h-8 w-6 sm:w-8 animate-spin" />
                </div>
                <p className="text-gray-400">Loading your creative content...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-8 p-4 sm:px-6 md:px-8">
              {/* Stats Grid */}
              <motion.div
                ref={metricsRef}
                initial="hidden"
                animate={metricsInView ? "visible" : "hidden"}
                variants={CARD_VARIANTS}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-4 sm:mb-8"
              >
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: { 
                          delay: index * 0.1,
                          type: "spring",
                          stiffness: 300,
                          damping: 30
                        }
                      }
                    }}
                  >
                    <Card className="h-full bg-gray-800/50 hover:shadow-lg transition-all duration-200 overflow-hidden group
                                   active:scale-98 transform cursor-pointer rounded-2xl border border-gray-700/50">
                      <CardContent className="p-4 sm:p-8">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-400">{stat.title}</p>
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-200">{stat.value}</h3>
                          </div>
                          <div className={cn(
                            'px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-sm font-medium transition-transform group-hover:scale-105',
                            stat.change.startsWith('+') ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                          )}>
                            {stat.change}
                          </div>
                        </div>
                        <div className="mt-4 sm:mt-6 flex items-center text-sm text-gray-400">
                          {stat.platform === 'instagram' ? (
                            <Instagram className="w-4 h-4 mr-2" />
                          ) : (
                            <TiktokIcon className="w-4 h-4 mr-2" />
                          )}
                          via {stat.platform.charAt(0).toUpperCase() + stat.platform.slice(1)}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>

              {/* Content Performance and Growth Opportunities */}
              <motion.div
                ref={insightsRef}
                initial="hidden"
                animate={insightsInView ? "visible" : "hidden"}
                variants={CARD_VARIANTS}
                className="mb-4 sm:mb-8"
              >
                <Card className="overflow-hidden rounded-2xl bg-gray-800/50 border border-gray-700/50">
                  <CardHeader className="border-b border-gray-700/50 bg-gradient-to-r from-gray-800/50 to-gray-700/50 p-4 sm:p-8">
                    <CardTitle className="flex items-center gap-3 text-lg sm:text-xl text-gray-200">
                      <Trophy className="w-5 h-5 text-purple-400" />
                      Top Performing Content
                      {loading && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-4 sm:space-y-6">
                    {contentInsights[0].metrics.map(([title, data], index) => (
                      <div key={index} className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-300">{title}</span>
                          <span className="text-sm text-gray-400">
                            {data.stats ? data.stats : '-'}
                          </span>
                        </div>
                        {Object.keys(data).length > 0 ? (
                          <div className="flex items-center gap-3">
                            <span className="text-base sm:text-lg font-medium text-gray-200 flex-1 truncate" title={data.title}>
                              {data.title}
                            </span>
                            {data.platform?.name === 'instagram' ? (
                              <Instagram className="w-4 h-4 text-pink-400 flex-shrink-0" />
                            ) : (
                              <TiktokIcon className="w-4 h-4 text-gray-200 flex-shrink-0" />
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic">
                            {loading ? 'Loading...' : 'No data available'}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* iOS-style Bottom Sheet */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 bg-gray-800/90 backdrop-blur-lg rounded-t-3xl shadow-2xl transform border-t border-gray-700/50"
        initial={{ y: "100%" }}
        animate={{ y: "95%" }}
        whileHover={{ y: "50%" }}
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.2}
      >
        <div className="p-4">
          <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-4" />
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-200">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-4">
              <motion.button
                className="p-4 rounded-2xl bg-gray-700/50 hover:bg-gray-700 flex flex-col items-center justify-center"
                whileTap={{ scale: 0.95 }}
              >
                <Zap className="w-6 h-6 text-purple-400 mb-2" />
                <span className="text-sm font-medium text-gray-200">New Post</span>
              </motion.button>
              <motion.button
                className="p-4 rounded-2xl bg-gray-700/50 hover:bg-gray-700 flex flex-col items-center justify-center"
                whileTap={{ scale: 0.95 }}
              >
                <Share2 className="w-6 h-6 text-purple-400 mb-2" />
                <span className="text-sm font-medium text-gray-200">Share</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
} 